// useNotifications — push-rule-driven desktop notifications.
//
// Responsibilities:
//
//   1. Subscribe to every joined room's timeline and, for each new
//      event, ask the SDK what push actions apply. When the server
//      says "notify" and the app isn't focused, fire a desktop
//      notification.
//   2. Request permission once on first successful login if the user
//      has never been prompted. The permission banner handles the
//      "user said no" case on its own — and on web it doubles as the
//      user-gesture path for browsers (Firefox, Safari) that reject
//      programmatic `Notification.requestPermission()` calls.
//   3. Expose a `bannerState` + `triggerEnable` pair that drives
//      `<PermissionBanner />`. On Tauri a "denied + not prompted"
//      distinction is kept in localStorage so a silent denial
//      doesn't re-prompt on every launch.
//
// Design decisions:
//
//   - Per-room subscriptions over a firehose. The Matrix wrapper's
//     unit of subscription is a room; subscribing per-room is
//     idiomatic and cheap (the timelines are already subscribed-to
//     by the active Timeline component, so this is a second cheap
//     fan-out in the same buffer). The real SDK may prefer a
//     coarser hook once we have it; at that point the fan-out moves
//     into one `subscribe` call inside `matrix.ts`.
//   - Dedup via a per-room "last seen event id" cursor. We mark the
//     tail event id on first subscription snapshot as the baseline —
//     events newer than that trigger a notification when push
//     actions say so. This suppresses the snapshot-as-noise problem.
//   - Safe cursor recovery: if the cursor falls out of the timeline
//     buffer (a deep-link jump, a cold restart that evicted old
//     events), we re-seed rather than notify for every buffered
//     event. This prevents a "notification storm" — the user would
//     otherwise get dozens of pings for messages they already read.
//   - Per-event dedup tag so grouped bursts don't collapse to a
//     single OS notification.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useDnd } from "~/hooks/useDnd";
import {
  focusApp,
  getPermission,
  requestPermission,
  showNotification,
  type NotificationPermission,
} from "~/lib/notifications";
import {
  getPushActionsForEvent,
  subscribeTimeline,
  type Client,
  type RoomSummary,
  type TimelineEvent,
} from "~/lib/matrix";
import { platform } from "~/lib/platform";

/** Max chars of body text displayed in the system notification. The
 *  brief pins 80; the ticket body says 100. We take the stricter
 *  value (80) so the OS isn't forced to ellipsise further. */
const BODY_TRUNCATE_LEN = 80;

const BANNER_DISMISS_KEY = "chat.notifications.bannerDismissedAt";
// Tauri's plugin collapses "user denied" into "not granted", which
// collides with the "not prompted yet" state. Persist the outcome of
// the first prompt on Tauri so subsequent boots can distinguish
// "never asked" from "asked and denied". On web `Notification.permission`
// is already authoritative — we never read / write this on that
// platform. Kept as a top-level constant so callers can't write to a
// `undefined` key under a misconfigured build.
const PROMPT_OUTCOME_KEY = "chat.notifications.promptOutcome";

/**
 * Shape the `<PermissionBanner />` renders. `"hidden"` covers every
 * branch the banner should not appear in (granted, default
 * pre-prompt, explicitly dismissed).
 */
export type NotificationBannerState = "hidden" | "denied";

export interface UseNotificationsResult {
  /** `"granted" | "denied" | "default"` or `null` until the initial
   *  probe lands. */
  permission: NotificationPermission | null;
  /** Whether the permission banner should render. */
  bannerState: NotificationBannerState;
  /** Invoked from a user gesture (the banner's Enable button).
   *  Triggers the platform prompt and refreshes permission state. */
  enableFromBanner: () => void;
  /** Dismiss the banner. Persists in localStorage so a page reload
   *  doesn't reopen it. */
  dismissBanner: () => void;
}

export function useNotifications(
  client: Client | null,
  rooms: RoomSummary[],
): UseNotificationsResult {
  const { setActiveRoomId, userId } = useMatrix();
  const dnd = useDnd(client);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  // Tracks whether the user has dismissed the denied banner. Read
  // synchronously on mount so the first render already reflects the
  // persisted dismissal — otherwise a dismissed banner would flash
  // back into view on every reload.
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(BANNER_DISMISS_KEY) !== null;
    } catch {
      return false;
    }
  });

  // Per-room cursor — event id of the last event we've either
  // notified on or seen in the initial snapshot. Keeping this in a
  // ref (rather than state) means the subscription callback can read
  // and write without triggering re-renders.
  const seenTailRef = useRef<Map<string, string>>(new Map());
  // Track the current client identity so a subscription from a
  // previous client (after re-login / token refresh that swaps
  // bindings) can be torn down rather than reused against the new
  // one. Two-refs: one for the id, another captured in the closure
  // at subscribe time — the snapshot callback ignores itself when
  // the id doesn't match the current one.
  const clientIdRef = useRef<Client | null>(client);
  // Latest room summaries keyed by id. The subscription callback
  // reads from this rather than the closed-over `room` argument so
  // rename / avatar updates surface in subsequent notifications
  // without tearing down the subscription. Assigning inside a
  // commit-phase effect keeps render pure — a discarded render
  // (React 18 concurrent rendering) no longer mutates the ref the
  // callback reads.
  const roomsByIdRef = useRef<Map<string, RoomSummary>>(new Map());
  useEffect(() => {
    const map = new Map<string, RoomSummary>();
    for (const r of rooms) map.set(r.id, r);
    roomsByIdRef.current = map;
  }, [rooms]);
  // Keep the latest nav handler in a ref so the subscription
  // callback never captures a stale `setActiveRoomId`.
  const navRef = useRef({ setActiveRoomId });
  navRef.current = { setActiveRoomId };
  const userIdRef = useRef<string | null>(userId);
  userIdRef.current = userId;
  const dndActiveRef = useRef(dnd.active);
  dndActiveRef.current = dnd.active;

  // Initial permission probe. Runs once per mount — the return value
  // is the platform's stored decision, not a prompt. The prompt
  // itself happens in the next effect, and only if the user has
  // never been asked.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await getPermission();
        if (cancelled) return;
        // On Tauri, distinguish "denied" from "not prompted" via the
        // persisted outcome — the plugin API conflates them into a
        // single not-granted state. Web's `Notification.permission`
        // is already authoritative so we skip the localStorage path
        // entirely there.
        if (platform === "tauri" && p !== "granted" && typeof window !== "undefined") {
          try {
            const saved = window.localStorage.getItem(PROMPT_OUTCOME_KEY);
            if (saved === "denied") {
              setPermission("denied");
              return;
            }
          } catch {
            /* swallow — localStorage unavailable */
          }
        }
        setPermission(p);
      } catch (err) {
        console.warn("useNotifications: getPermission failed", err);
        if (!cancelled) setPermission("denied");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // First-login prompt. Trigger once on the first render where we
  // have a live client (logged in) AND the permission state is
  // explicitly `"default"` — meaning the user has never been asked
  // on this device. The prompt resolves to `"granted"` or
  // `"denied"`; we mirror the result into state so the banner can
  // react. Note that on web this effect's call is NOT inside a
  // user gesture — Firefox and Safari may silently reject it,
  // leaving permission at `"default"`. The banner's Enable button
  // is the guaranteed user-gesture fallback; we surface the banner
  // for both `"denied"` and "tried-once-still-default" states.
  const [autoPromptAttempted, setAutoPromptAttempted] = useState(false);
  const promptedRef = useRef(false);
  useEffect(() => {
    if (!client) return;
    if (promptedRef.current) return;
    if (permission !== "default") return;
    promptedRef.current = true;
    void (async () => {
      await runPrompt(setPermission);
      setAutoPromptAttempted(true);
    })();
  }, [client, permission]);

  // Main subscription. Re-subscribes when the rooms list changes —
  // a room join/leave updates the membership, and we want the new
  // room included in the fan-out without tearing down the existing
  // ones. Adding / removing subscriptions incrementally (rather
  // than rebuilding on every render) keeps the overhead near zero:
  // a rooms array that changes one entry rebuilds one subscription,
  // not all of them.
  const roomSubsRef = useRef<Map<string, () => void>>(new Map());
  // Stable id list — `useMemo` over the id strings means the
  // subscription effect only re-runs when the set of joined rooms
  // actually changes, not on every `rooms` re-identification from
  // the context layer.
  const roomIdList = useMemo(() => rooms.map((r) => r.id).join("|"), [rooms]);
  useEffect(() => {
    // Client-identity change: drop every subscription keyed on the
    // previous client. The real SDK re-emits fresh snapshots on the
    // new client, so rebuilding from scratch is honest. Without
    // this, the old subscription's callback keeps firing against a
    // stale bindings map.
    if (clientIdRef.current !== client) {
      for (const unsub of roomSubsRef.current.values()) unsub();
      roomSubsRef.current.clear();
      seenTailRef.current.clear();
      clientIdRef.current = client;
    }
    if (!client) {
      // Tear everything down when the client evaporates (sign-out).
      for (const unsub of roomSubsRef.current.values()) unsub();
      roomSubsRef.current.clear();
      seenTailRef.current.clear();
      return;
    }
    const nextIds = new Set(rooms.map((r) => r.id));
    // Drop subscriptions for rooms that fell out of the list.
    for (const [id, unsub] of roomSubsRef.current.entries()) {
      if (!nextIds.has(id)) {
        unsub();
        roomSubsRef.current.delete(id);
        seenTailRef.current.delete(id);
      }
    }
    // Add subscriptions for new rooms. The first snapshot calibrates
    // the cursor — nothing fires for existing history, only for
    // events that land after this point.
    for (const room of rooms) {
      if (roomSubsRef.current.has(room.id)) continue;
      const subscribedRoomId = room.id;
      const unsub = subscribeTimeline(client, subscribedRoomId, (events) => {
        // Look up the *current* room summary (rename etc.) rather
        // than closing over the stale `room` from subscribe-time.
        const freshRoom = roomsByIdRef.current.get(subscribedRoomId);
        if (!freshRoom) return;
        handleRoomSnapshot({
          client,
          room: freshRoom,
          events,
          seenTailRef,
          navRef,
          userIdRef,
          dndActiveRef,
        });
      });
      roomSubsRef.current.set(subscribedRoomId, unsub);
    }
    // `roomIdList` identifies the dependent state precisely — only
    // a set-membership change re-runs this effect, not every
    // rooms-emission re-identification. The `rooms` and `client`
    // reads inside the body are read-on-entry; the `client` check
    // above covers the re-login case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, roomIdList]);

  // Teardown on unmount. Snapshot the ref into a local so the
  // cleanup doesn't read `.current` after React has nulled it.
  useEffect(() => {
    const subs = roomSubsRef.current;
    return () => {
      for (const unsub of subs.values()) unsub();
      subs.clear();
    };
  }, []);

  // Banner visibility: surface it when the user has explicitly
  // denied permission AND when the auto-prompt attempt left us
  // stuck on `"default"` (Firefox / Safari silently ignoring the
  // effect-driven call). In the latter case the banner's Enable
  // button is the user's only gesture path to the prompt.
  const needsGestureFallback = permission === "default" && autoPromptAttempted && client !== null;
  const bannerState: NotificationBannerState =
    (permission === "denied" || needsGestureFallback) && !bannerDismissed ? "denied" : "hidden";

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      window.localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now()));
    } catch {
      /* swallow — localStorage may be full / disabled */
    }
  }, []);

  const enableFromBanner = useCallback(() => {
    // This is the critical user-gesture path: Firefox and Safari
    // only show the prompt when `Notification.requestPermission()`
    // is invoked synchronously from a click / keydown. The handler
    // is therefore a plain function that kicks off the async call
    // without awaiting — the browser's gesture token expires at the
    // end of this callback stack.
    void runPrompt(setPermission);
  }, []);

  return { permission, bannerState, enableFromBanner, dismissBanner };
}

/**
 * Shared prompt-and-persist path. Used by both the first-login
 * auto-prompt and the banner's user-gesture button.
 */
async function runPrompt(setPermission: (next: NotificationPermission) => void): Promise<void> {
  try {
    const next = await requestPermission();
    setPermission(next);
    // Only Tauri needs the localStorage distinguisher — on web the
    // `Notification.permission` API is already authoritative for
    // telling "never asked" (`default`) apart from "asked & denied"
    // (`denied`).
    if (platform === "tauri" && typeof window !== "undefined") {
      try {
        if (next === "denied") {
          window.localStorage.setItem(PROMPT_OUTCOME_KEY, "denied");
        } else if (next === "granted") {
          // Clear any stale denied marker so a user who says yes on
          // the second try doesn't stay flagged as denied.
          window.localStorage.removeItem(PROMPT_OUTCOME_KEY);
        }
      } catch {
        /* swallow — localStorage unavailable */
      }
    }
  } catch (err) {
    console.warn("useNotifications: requestPermission failed", err);
    setPermission("denied");
  }
}

/**
 * Handle one timeline snapshot for `room`. Called on both the
 * synchronous initial snapshot (where we only seed the cursor) and
 * every subsequent change (where we diff + notify). Pure helpers
 * keep the effect body readable.
 */
function handleRoomSnapshot({
  client,
  room,
  events,
  seenTailRef,
  navRef,
  userIdRef,
  dndActiveRef,
}: {
  client: Client;
  room: RoomSummary;
  events: TimelineEvent[];
  seenTailRef: React.MutableRefObject<Map<string, string>>;
  navRef: React.MutableRefObject<{
    setActiveRoomId: (id: string | null) => void;
  }>;
  userIdRef: React.MutableRefObject<string | null>;
  dndActiveRef: React.MutableRefObject<boolean>;
}): void {
  // Empty snapshot — freshly-joined room whose timeline hasn't
  // paginated yet, or the SDK cleared its buffer. We do NOT seed the
  // cursor: the *first non-empty* snapshot below takes the
  // `cursor === undefined` branch, seeds against the real tail, and
  // stays silent. That absorb-then-notify behaviour matches what
  // every other Matrix client does on room join and avoids spamming
  // the user with historical messages they've already read on
  // another device.
  if (events.length === 0) return;
  const lastId = events[events.length - 1]!.id;
  const cursor = seenTailRef.current.get(room.id);
  // First snapshot for this room — seed the cursor and don't fire.
  // This is the "no notification storm on first sync" rule.
  if (cursor === undefined) {
    seenTailRef.current.set(room.id, lastId);
    return;
  }
  if (cursor === lastId) return;
  // Walk backwards from the tail until we hit the cursor. Any event
  // after the cursor is a candidate. Using a reverse walk (rather
  // than `indexOf` + slice) keeps the cost bounded by the number of
  // new events, not the buffer size.
  const candidates: TimelineEvent[] = [];
  let foundCursor = false;
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const ev = events[i]!;
    if (ev.id === cursor) {
      foundCursor = true;
      break;
    }
    candidates.push(ev);
  }
  // Advance cursor first so a handler that throws doesn't cause us
  // to re-fire on the next snapshot.
  seenTailRef.current.set(room.id, lastId);
  // Cursor not in buffer — the SDK trimmed history or we crossed a
  // /sync-reset boundary. Re-seeding (skipping notifications for
  // this snapshot) is much safer than firing for every buffered
  // event, which would spam the user with messages they already
  // read.
  if (!foundCursor) return;
  if (candidates.length === 0) return;
  // Chronological order so grouped bursts fire oldest-first.
  candidates.reverse();
  const myUserId = userIdRef.current;
  const { setActiveRoomId } = navRef.current;
  // The cursor was already advanced above. During DND we intentionally
  // drop candidates instead of queueing them for a later notification storm.
  if (dndActiveRef.current) return;
  for (const ev of candidates) {
    // Skip our own events. When the user id hasn't landed yet (race
    // between client-ready and `MatrixContext` reading it off the
    // bindings), skip the event entirely rather than risk notifying
    // the user about their own just-sent message.
    if (!myUserId) continue;
    if (ev.sender.id === myUserId) continue;
    const actions = getPushActionsForEvent(client, ev.id);
    if (!actions || !actions.notify) continue;
    if (typeof document !== "undefined" && document.hasFocus()) continue;
    const body = deriveNotificationBody(ev);
    if (!body) continue;
    void showNotification({
      title: buildTitle(ev.sender.displayName, room),
      body,
      // Per-event tag so a grouped burst of N events shows as N
      // OS notifications rather than collapsing to one — that
      // collapse would lose information the chronological walk
      // above was explicitly preserving.
      tag: `nurion-chat:${room.id}:${ev.id}`,
      onClick: () => {
        void focusApp();
        setActiveRoomId(room.id);
      },
    });
  }
}

function buildTitle(senderDisplayName: string, room: RoomSummary): string {
  // Honour the design brief: "<sender-displayName> in <#room-name>".
  // DMs sit with `@`; channels with `#`. Falling back to the room id
  // only if `room.name` is missing — the stub + real SDK both
  // populate this field.
  const prefix = room.isDirect ? "@" : "#";
  const label = room.name || room.id;
  return `${senderDisplayName} in ${prefix}${label}`;
}

function deriveNotificationBody(event: TimelineEvent): string {
  const c = event.content;
  let raw: string;
  switch (c.type) {
    case "m.text":
    case "m.emote":
    case "m.notice":
      raw = c.body;
      break;
    case "m.image":
      raw = `Sent an image${c.body ? `: ${c.body}` : ""}`;
      break;
    case "m.video":
      raw = `Sent a video${c.body ? `: ${c.body}` : ""}`;
      break;
    case "m.file":
      raw = `Sent a file${c.body ? `: ${c.body}` : ""}`;
      break;
    default:
      // State events and other kinds don't produce a notification.
      return "";
  }
  if (raw.length <= BODY_TRUNCATE_LEN) return raw;
  return `${raw.slice(0, BODY_TRUNCATE_LEN - 1)}…`;
}
